/**
 * RTWO Blockchain Core Foundation — Domain Models
 * RTWO v2.2 | Module ID: RTWO-BLCK-001
 *
 * Read-only blockchain data layer for Explorer and runtime APIs.
 * No consensus — blocks are served from a connected backend only.
 *
 * Truthful Data Policy:
 * - No mock blocks, fabricated heights, or fake hashes.
 * - Return Unavailable until a real backend exists.
 */

export const BLOCKCHAIN_CORE_VERSION = {
  moduleId: 'RTWO-BLCK-001',
  moduleName: 'Blockchain Core Foundation',
  version: '2.2.0-blockchain-core-foundation',
  phase: 'RTWO v2.2 — Blockchain Core Foundation',
  status: 'Read Layer Ready — Backend Not Connected',
  productionReadiness: 'Pending Real Blockchain Backend Connection',
  policy: 'Truthful Data Policy Enforced — No Mock Blocks, Fabricated Heights, or Fake Hashes',
} as const;

export type BlockchainCoreDataStatus = 'not_configured' | 'unavailable' | 'available';

export type BlockchainCoreLookupType = 'height' | 'hash';

export interface BlockchainCoreBlock {
  blockId: string;
  blockNumber: string | null;
  blockHash: string | null;
  parentHash: string | null;
  timestamp: number | null;
  transactionCount: number | null;
  proposer: string | null;
  source: string;
}

export interface LatestBlockResult {
  moduleId: 'RTWO-BLCK-001';
  phase: string;
  available: boolean;
  status: BlockchainCoreDataStatus;
  label: string;
  block: BlockchainCoreBlock | null;
  blockCount: number;
  backendConnected: boolean;
  consensusEnabled: boolean;
  collectedAt: number;
  source: string;
}

export interface BlockLookupResult {
  moduleId: 'RTWO-BLCK-001';
  phase: string;
  available: boolean;
  status: BlockchainCoreDataStatus;
  label: string;
  lookupType: BlockchainCoreLookupType | null;
  lookupValue: string | null;
  block: BlockchainCoreBlock | null;
  backendConnected: boolean;
  consensusEnabled: boolean;
  collectedAt: number;
  source: string;
}

export interface BlockchainCoreCheck {
  id: string;
  label: string;
  category: string;
  passed: boolean;
  note: string;
}

export interface BlockchainCoreValidationResult {
  moduleId: 'RTWO-BLCK-001';
  phase: string;
  passed: boolean;
  coreEnabled: boolean;
  backendConnected: boolean;
  consensusEnabled: boolean;
  latestBlock: LatestBlockResult;
  blockLookup: BlockLookupResult;
  checks: BlockchainCoreCheck[];
  blockers: string[];
  collectedAt: number;
  source: string;
}
